import React, { useEffect, useRef, useState } from "react";
import { Button, Flex, Input, Box, Container, Center, Image, Heading, Stack, VStack, HStack, SimpleGrid } from "@chakra-ui/react";
import { colorMain } from "../constants/themes";
import {
  handleGenerateStatements,
  handleGetStatementDetails,
  isCreditPayment,
} from "../utils/api";
import { createFileName, useScreenshot } from "use-react-screenshot";
import { useNavigate, useParams } from "react-router";
import LoadingPage from "./LoadingPage";
import FourOFour from "./404";
import { ToastContainer, toast } from "react-toastify";

import { Text, Circle, Spinner, Card, Icon } from "@chakra-ui/react";
import { WarningIcon, SearchIcon, CheckCircleIcon } from "@chakra-ui/icons";
import PaymentConfirmation from "./Features/PaymentConfirmation";
import {
  formatterPaidBy,
  titleCase,
  downloadPdf,
  formatterTimePaid,
} from "../utils/functions";
import { AiFillCheckCircle } from "react-icons/ai";
import collectLogo from "../assets/img/nobukcollect.png";

const StatementDownload = () => {
  const navigate = useNavigate();
  let { mpesaCode } = useParams();
  const [processing, setProcessing] = useState(false);
  const [data, setData] = useState(false);
  const [load, setLoad] = useState(true);
  const [clicked, setClicked] = useState(false);
  const [time, setTime] = useState(100);
  const [searchMpesaCode, setSearchMpesaCode] = useState("");
  setTimeout(() => {
    setTime(0);
  }, 60000);
  const ref = useRef(null);
  const refs = useRef(null);
  const initialRef = useRef();

  const [image, takeScreenshot] = useScreenshot({
    type: "image/png",
    quality: 1.0,
  });

  const [downloading, setIsDownloading] = useState(false);
  const [statement, setStatement] = useState(true);

  const zenColor = "#062a30";

  // CREDIT rows are the amount still to be paid, not money collected
  const allTransactions = data?.transactions || [];
  const paidTransactions = allTransactions.filter((t) => !isCreditPayment(t));
  const totalPaid = paidTransactions.reduce(
    (acc, payment) => acc + payment.payment_amount,
    0
  );
  const amountDue = allTransactions
    .filter(isCreditPayment)
    .reduce((acc, payment) => acc + payment.payment_amount, 0);
  const formatMoney = (value) =>
    Number(value).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  useEffect(() => {
    const fetchDetails = async () => {
      if (mpesaCode) {
        setClicked(true);
        toast.info("Checking records...");
        const result = await handleGetStatementDetails(mpesaCode);
        if (result.success) {
          setData(result.data);
          toast.success(result.message);
        } else {
          toast.warning(result.message);
        }
        setClicked(false);
        setLoad(false);
      } else {
        setLoad(false);
      }
    };
    fetchDetails();
  }, [mpesaCode]);

  useEffect(() => {
    if (!mpesaCode) {
      initialRef?.current?.focus();
      toast.warning(`Please enter an MPESA code to search!`);
    }
  }, []);

  useEffect(() => {
    if (!load && !data && time !== 0 && !searchMpesaCode) {
      initialRef?.current?.focus();
      toast.warning(`Please enter an MPESA code to search!`);
    }
  }, [load]);

  const getImage = async () => {
    if (statement) {
      setStatement(false);
      return;
    }

    setIsDownloading(true);

    try {
      const dataUri = await takeScreenshot(refs.current);
      downloadImage(dataUri);
    } catch (error) {
      console.error("Error capturing screenshot:", error);
    }

    setIsDownloading(false);
    setStatement(true);
  };

  useEffect(() => {
    if (!statement) {
      getImage();
    }
  }, [statement]);

  const handleSearch = () => {
    if (searchMpesaCode.trim()) {
      navigate(`/statement/${searchMpesaCode.trim()}`);
    } else {
      toast.error("Please enter a valid Mpesa code.");
    }
  };

  const downloadImage = (dataUri) => {
    setIsDownloading(true);
    const link = document.createElement("a");
    link.href = dataUri;
    link.download = "statement.png";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    setIsDownloading(false);
  };

  let search = (
    <>
      <ToastContainer />
      <Box
        bg="white"
        borderRadius="2xl"
        shadow="xl"
        p={6}
        position="relative"
        overflow="hidden"
      >
        <Flex alignItems="center" justifyContent="center" mb={4}>
          <Circle
            bg="gray.50"
            size="16"
          >
            <SearchIcon fontSize="24px" color={zenColor} />
          </Circle>
        </Flex>
        <Text fontWeight="bold" fontSize="xl" textAlign="center" color={zenColor} mb={2}>
          Get Your Payments Statement for a collection!
        </Text>
        <Text fontSize="md" color="gray.500" textAlign="center" mb={6}>
          Please enter a MPESA confirmation code of the last payment you made to the collection
        </Text>
        <Flex
          flexDirection="column"
          alignItems="center"
          gap={4}
        >
          <Input
            placeholder="Enter Mpesa Code"
            value={searchMpesaCode}
            onChange={(e) => setSearchMpesaCode(e.target.value)}
            size="lg"
            borderColor="gray.300"
            borderRadius="md"
            focusBorderColor={zenColor}
            ref={initialRef}
          />
          <Button
            bg={zenColor}
            color="white"
            size="lg"
            width="full"
            borderRadius="md"
            _hover={{ bg: "#002a35" }}
            onClick={handleSearch}
            isDisabled={!searchMpesaCode || (searchMpesaCode && searchMpesaCode?.length !== 10)}
            fontWeight="bold"
          >
            Search
          </Button>
        </Flex>
      </Box>
    </>
  );

  return (
    <Box minH="100vh" bg="#fbfdfd" py={8} px={4} fontFamily="'Poppins', sans-serif" display="flex" alignItems="center" justifyContent="center">
      {data && (
        <Container maxW="md" p={0}>
          <ToastContainer />
          {clicked ? (
            <Flex
              h="300px"
              flexDir="column"
              alignItems="center"
              justifyContent="center"
              bg="white"
              borderRadius="3xl"
              shadow="2xl"
            >
              <Spinner
                thickness="4px"
                speed="0.65s"
                emptyColor="gray.200"
                color={zenColor}
                size="xl"
              />
              <Text fontSize="md" mt={4} color="gray.600" fontWeight="medium">Checking records...</Text>
            </Flex>
          ) : (
            <>
              <Box
                bg="white"
                borderRadius="3xl"
                shadow="2xl"
                overflow="hidden"
                ref={refs}
                p={8}
                position="relative"
              >
                <Center mb={2}>
                  <Image src={collectLogo} w="100px" />
                </Center>
                <Heading size="lg" color="#062a30" mb={2} textAlign="center" textTransform="uppercase" letterSpacing="widest">
                  STATEMENT
                </Heading>
                <Text fontSize="sm" color="gray.500" textAlign="center" mb={6} fontWeight="medium" textTransform="uppercase" letterSpacing="wide">
                  {data?.collection || "N/A"}
                </Text>

                <Box w="100%" h="1px" bg="gray.200" mb={6} />

                {/* 2x2 Grid Highlights */}
                <SimpleGrid columns={2} spacing={6} w="100%" mb={8} textAlign="center">
                  <Flex direction="column">
                    <Text fontSize="xs" color="gray.500" textTransform="uppercase" fontWeight="bold">Total Collected</Text>
                    <Text fontSize="lg" fontWeight="extrabold" color="#062a30">
                      KES {formatMoney(totalPaid)}
                    </Text>
                  </Flex>
                  {amountDue > 0 ? (
                    <Flex
                      direction="column"
                      bg="orange.50"
                      border="1px solid"
                      borderColor="orange.300"
                      borderRadius="lg"
                      py={2}
                    >
                      <Text fontSize="xs" color="orange.700" textTransform="uppercase" fontWeight="bold">Amount to be Paid</Text>
                      <Text fontSize="lg" fontWeight="extrabold" color="orange.600">
                        KES {formatMoney(amountDue)}
                      </Text>
                    </Flex>
                  ) : (
                    <Flex direction="column">
                      <Text fontSize="xs" color="gray.500" textTransform="uppercase" fontWeight="bold">Transactions</Text>
                      <Text fontSize="lg" fontWeight="extrabold" color="#062a30">
                        {paidTransactions.length}
                      </Text>
                    </Flex>
                  )}
                  <Flex direction="column">
                    <Text fontSize="xs" color="gray.500" textTransform="uppercase" fontWeight="bold">From</Text>
                    <Text fontSize="md" fontWeight="bold" color="#062a30" noOfLines={1}>
                      {data?.phone || data?.transactions?.[0]?.customer_phone || "N/A"}
                    </Text>
                  </Flex>
                  <Flex direction="column">
                    <Text fontSize="xs" color="gray.500" textTransform="uppercase" fontWeight="bold">Name</Text>
                    <Text fontSize="md" fontWeight="bold" color="#062a30" noOfLines={1}>
                      {data?.name || titleCase(formatterPaidBy(data?.paid_by)) || "Anonymous"}
                    </Text>
                  </Flex>
                </SimpleGrid>

                <Box w="100%" h="1px" bg="gray.200" mb={6} />

                {/* Recent Activity (Top 5 Sorted) */}
                {paidTransactions.length > 0 && (
                  <Box w="100%" mb={8}>
                    <Heading size="xs" color="gray.400" mb={4} textTransform="uppercase" letterSpacing="widest" textAlign="center">
                      Recent Activity
                    </Heading>
                    <VStack spacing={3} align="stretch">
                      {[...paidTransactions]
                        .sort((a, b) => new Date(b.payment_time) - new Date(a.payment_time))
                        .slice(0, 5)
                        .map((txn, index) => (
                          <Flex key={index} justify="space-between" align="center" bg="gray.50" p={3} borderRadius="lg">
                            <Box>
                              <Text fontWeight="bold" color="#062a30" fontSize="xs">
                                {txn.account ? titleCase(txn.account) : "N/A"}
                              </Text>
                              <Text fontSize="xs" color="gray.500">
                                {formatterTimePaid(txn.payment_time)}
                              </Text>
                            </Box>
                            <Text fontWeight="bold" color="#062a30" fontSize="sm">
                              +{Number(txn.payment_amount).toLocaleString()}
                            </Text>
                          </Flex>
                        ))}
                    </VStack>

                    {/* View More Option */}
                    {paidTransactions.length > 5 && (
                      <Button variant="link" color="#2fb5b0" size="sm" w="100%" mt={2} onClick={() => {
                        window.open(`/statement/${data?.transcode || mpesaCode || searchMpesaCode}/full`, "_self"); // Placeholder action
                        toast.info("Download full statement for all details!");
                      }}>
                        View all {paidTransactions.length} transactions
                      </Button>
                    )}
                  </Box>
                )}

                <Button
                  bg="#062a30"
                  color="white"
                  size="lg"
                  width="full"
                  py={7}
                  borderRadius="xl"
                  _hover={{ bg: "#000000", transform: "translateY(-1px)", shadow: "lg" }}
                  _active={{ bg: "#062a30", transform: "translateY(0)" }}
                  fontSize="md"
                  fontWeight="semibold"
                  isDisabled={processing || downloading}
                  isLoading={processing || downloading}
                  loadingText="Processing..."
                  onClick={async () => {
                    if (data && data.transactions) {
                      setProcessing(true);
                      const result = await handleGenerateStatements({
                        details: {
                          ...data,
                          sales_date: data.sales_date || new Date().getTime(),
                        },
                        number:
                          data.phone ||
                          data?.transactions?.[0]?.customer_phone ||
                          "2547********",
                        paid_by: data.name || "Anonymous",
                        from: true,
                      });

                      if (result.success) {
                        toast.success("Download started!");
                        downloadPdf(result.base64, result.fileName);
                      } else {
                        toast.error(result.message || "Download Failed!");
                      }
                      setProcessing(false);
                    } else {
                      toast.error("Please refresh and try again!");
                    }
                  }}
                >
                  Download Statement PDF
                </Button>
              </Box>
            </>
          )}
        </Container>
      )}

      {!mpesaCode && (
        <Container maxW="md" p={0}>
          {search}
        </Container>
      )}

      {(load || time === 60000) && <LoadingPage />}

      {!load && !data && time === 0 && mpesaCode && <FourOFour />}
      {!load && !data && time !== 0 && !searchMpesaCode && mpesaCode && (
        <Container maxW="md" p={0}>
          <ToastContainer />
          <Box
            bg="white"
            borderRadius="3xl"
            shadow="2xl"
            overflow="hidden"
            p={8}
            textAlign="center"
          >
            <Flex justifyContent="center" mb={6}>
              <Circle bg="red.50" size="20">
                <WarningIcon fontSize="40px" color="red.500" />
              </Circle>
            </Flex>
            <Heading size="lg" color="#062a30" mb={2}>
              No Record Found
            </Heading>
            <Text color="gray.500" fontSize="sm" mb={4}>
              We couldn't find a payment record. Please check your code.
            </Text>
            {search}
          </Box>
        </Container>
      )}
    </Box>
  );
};
export default StatementDownload;
